import React, { useEffect, useState } from "react";
import { formatStringWithPlaceholders, hasXSSCharacters, isEmail, isFullNameValid } from "../../../helpers/Utils";
import ICountry from "../../../models/Country.interface";
import { IInvoiceDetails } from "../../../models/InvoiceDetails.model";
import { ISenderDetails } from "../../../models/SenderDetails.model";
import DataService from "../../../services/DataService.service";
import { AppSettingContext } from "../../Application";

export interface IInvoiceDetailsProps {
    isBusinessPurchase?: boolean;
    model: IInvoiceDetails;
    senderDetails: ISenderDetails;
    handleUpdate(model: IInvoiceDetails): void;
}

const InvoiceDetails = (props: IInvoiceDetailsProps) => {
    const { labels } = React.useContext(AppSettingContext);
    const culture = document.documentElement.lang ?? "en-GB";

    //#region STATE PROPERTIES

    const [model, setModel] = useState({
        firstName: props.model?.firstName ?? props.senderDetails?.firstName ?? "",
        lastName: props.model?.lastName ?? props.senderDetails?.lastName ?? "",
        fullName: props.model?.fullName ?? props.senderDetails.fullName?.trim() ?? "",
        email: props.model?.email ?? props.senderDetails?.email ?? "",
        companyName: props.model?.companyName ?? props.senderDetails?.company ?? "",

        country: "IE", // by default
        county: props.model?.country ?? "",
        city: props.model?.city ?? "",
        postcode: props.model?.postcode ?? "",

        addressLine1: props.model?.addressLine1 ?? "",
        addressLine2: props.model?.addressLine2 ?? "",
        addressLine3: props.model?.addressLine3 ?? "",
    } as IInvoiceDetails);
    const [countries, setCountries] = useState(Array<ICountry>(0));
    const [modelErrors, setModelErrors] = useState({
        fullName: '',
        email: '',
        companyName: '',
        country: '',
        city: '',
        postcode: '',
        addressLine1: ''
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        DataService.getCountries(culture)
            .then(response => {
                if (response) {
                    setCountries(response)
                } else {
                    console.error("Unable to get countries")
                }
            }).catch(console.error);
    }, []);

    useEffect(() => {
        props.handleUpdate(model);
    }, [model]);

    //#endregion

    //#region HANDLERS

    const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        if (hasXSSCharacters(value)) {
            event.preventDefault();
        }

        let errMsg = '';

        if (name === 'fullName') {
            if (!value || !value.trim()) {
                errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Full name");
            } else if (!isFullNameValid(value.trim())) {
                errMsg = "Invalid full name";
            }
        } else if (name === 'email') {
            if (!value.trim()) {
                errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Email");
            } else if (!isEmail(value)) {
                errMsg = labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.Email.ErrorMessage");
            }
        } else if (name === 'companyName' && !value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Company name");
        } else if (name === 'country' && !value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Country");
        } else if (name === 'city' && !value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "City");
        } else if (name === 'addressLine1' && !value.trim()) {
            errMsg = formatStringWithPlaceholders(labels.get("GiftCard.App.V2.Shared.ErrorMessages.FieldRequired"), "Address line 1");
        } else if (name === 'postcode' && value && value.trim() && value.trim().length < 3) {
            errMsg = "Invalid postcode";
        }

        setModelErrors({
            ...modelErrors,
            [name]: errMsg
        });

        setModel({
            ...model,
            [name]: value
        });
    };

    //#endregion

    return (
        <div className="invoice invoice-payment-group remove-all-margin">
            <h5>{labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Title")}</h5>
            <div className="tab remove-all-margin">
                <div className="flex-container">
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="fullName">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.FullName.Label")}
                        </label>
                        <input type="text"
                            name="fullName"
                            id="fullName"
                            maxLength={150}
                            autoComplete="off"
                            aria-autocomplete="none"
                            className={modelErrors.fullName ? "react-input-error" : ""}
                            value={model.fullName}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.FullName.Placeholder")}
                        />

                        {modelErrors.fullName && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.fullName}
                            </i>
                        )}
                    </div>
                    {/*<div className="form-field invoice-payment-input">*/}
                    {/*    <label htmlFor="firstName">*/}
                    {/*        {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.FirstName.Label")}*/}
                    {/*    </label>*/}
                    {/*    <input type="text"*/}
                    {/*        name="firstName"*/}
                    {/*        id="firstName"*/}
                    {/*        maxLength={150}*/}
                    {/*        autoComplete="off" aria-autocomplete="none"*/}
                    {/*        value={model.firstName}*/}
                    {/*        onChange={event => handleInput(event)}*/}
                    {/*        placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.FirstName.Placeholder")}*/}
                    {/*    />*/}
                    {/*</div>*/}
                    {/*<div className="form-field invoice-payment-input">*/}
                    {/*    <label htmlFor="lastName">*/}
                    {/*        {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.LastName.Label")}*/}
                    {/*    </label>*/}
                    {/*    <input type="text"*/}
                    {/*        name="lastName"*/}
                    {/*        id="lastName"*/}
                    {/*        maxLength={150}*/}
                    {/*        autoComplete="off" aria-autocomplete="none"*/}
                    {/*        value={model.lastName}*/}
                    {/*        onChange={event => handleInput(event)}*/}
                    {/*        placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.LastName.Placeholder")}*/}
                    {/*    />*/}
                    {/*</div>*/}
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="email">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.Email.Label")}
                        </label>
                        <input type="text"
                            name="email"
                            id="email"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            className={modelErrors.email ? "react-input-error" : ""}
                            value={model.email}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.Email.Placeholder")}
                        />

                        {modelErrors.email && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.email}
                            </i>
                        )}
                    </div>
                    {props.isBusinessPurchase && (
                        <div className="form-field invoice-payment-input">
                            <label htmlFor="companyName">
                                {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.CompanyName.Label")}
                            </label>
                            <input type="text"
                                name="companyName"
                                id="companyName"
                                maxLength={150}
                                autoComplete="off" aria-autocomplete="none"
                                className={modelErrors.companyName ? "react-input-error" : ""}
                                value={model.companyName}
                                onChange={event => handleInput(event)}
                                placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.CompanyName.Placeholder")}
                            />

                            {modelErrors.companyName && (
                                <i className="small-fs info react-input-error-message">
                                    {modelErrors.companyName}
                                </i>
                            )}
                        </div>
                    )}
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="country">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.Country.Label")}
                        </label>
                        <select name="country" id="country"
                            value={model.country}
                            onChange={event => {
                                setModel({ ...model, country: event.target.value })
                            }}
                        >
                            {(
                                () => {
                                    const options = [];

                                    for (let i = 0; i < countries.length; i++) {
                                        options.push(<option key={i} value={countries[i].code}>{countries[i].name}</option>);
                                    }

                                    return options;
                                }
                            )()}
                        </select>

                        {modelErrors.country && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.country}
                            </i>
                        )}
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="county">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.County.Label")}
                        </label>
                        <input type="text"
                            name="county"
                            id="county"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            value={model.county}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.County.Placeholder")}
                        />
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="city">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.City.Label")}
                        </label>
                        <input type="text"
                            name="city"
                            id="city"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            className={modelErrors.city ? "react-input-error" : ""}
                            value={model.city}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.City.Placeholder")}
                        />

                        {modelErrors.city && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.city}
                            </i>
                        )}
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="postCode">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.PostCode.Label")}
                        </label>
                        <input type="text"
                            name="postcode"
                            id="postcode"
                            className={modelErrors.postcode ? "react-input-error" : ""}
                            maxLength={150}
                            autoComplete="off"
                            value={model.postcode}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.PostCode.Placeholder")}
                        />

                        {modelErrors.postcode && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.postcode}
                            </i>
                        )}
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="addressLine1">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine1.Label")}
                        </label>
                        <input type="text"
                            name="addressLine1"
                            id="addressLine1"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            className={modelErrors.addressLine1 ? "react-input-error" : ""}
                            value={model.addressLine1}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine1.Placeholder")}
                        />

                        {modelErrors.addressLine1 && (
                            <i className="small-fs info react-input-error-message">
                                {modelErrors.addressLine1}
                            </i>
                        )}
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="addressLine2">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine2.Label")}
                        </label>
                        <input type="text"
                            name="addressLine2"
                            id="addressLine2"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            value={model.addressLine2}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine2.Placeholder")}
                        />
                    </div>
                    <div className="form-field invoice-payment-input">
                        <label htmlFor="addressLine3">
                            {labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine3.Label")}
                        </label>
                        <input type="text"
                            name="addressLine3"
                            id="addressLine3"
                            maxLength={150}
                            autoComplete="off" aria-autocomplete="none"
                            value={model.addressLine3}
                            onChange={event => handleInput(event)}
                            placeholder={labels.get("GiftCard.App.Steps.PreviewAndPay.Invoice.Labels.AddressLine3.Placeholder")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetails;